import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';
import { Mail, Lock, Loader2, Eye, EyeOff, Phone, ArrowLeft } from 'lucide-react';
import Msg91Widget from '../components/Msg91Widget';

export default function Login() {
  const nav = useNavigate();
  const { uiLang, checkAuth } = useApp();
  const t = useT(uiLang);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Iter-52.23 — phone-OTP login mode. Runs the MSG91 widget and
  // exchanges the verified access_token at /auth/login-with-mobile-otp.
  // Only rendered when the server flag is on.
  const [phoneOnlyEnabled, setPhoneOnlyEnabled] = useState(false);
  const [phoneMode, setPhoneMode] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneWidgetKey, setPhoneWidgetKey] = useState(0);

  useEffect(() => {
    api.get('/config/support').then(({ data }) => {
      setPhoneOnlyEnabled(Boolean(data.phone_only_signup_enabled));
    }).catch(() => {});
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + '/profiles';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.post('/auth/login', { email: email.trim(), password });
      await checkAuth();
      nav('/profiles');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not sign you in. Please try again.');
    } finally { setBusy(false); }
  };

  // Iter-52.23 — MSG91 widget → /auth/login-with-mobile-otp. Any user
  // whose mobile is on record can sign in this way, including
  // classic email+password users who don't want to type a password.
  const onPhoneOtpVerified = async (accessToken) => {
    setPhoneError('');
    setPhoneBusy(true);
    try {
      await api.post('/auth/login-with-mobile-otp', { access_token: accessToken });
      await checkAuth();
      nav('/profiles');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not sign you in. Please try again.';
      setPhoneError(detail);
    } finally {
      setPhoneBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F4B740] blur-3xl opacity-70" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#D97398] blur-3xl opacity-70" />

      <div className="relative z-10 max-w-md w-full text-center flex flex-col items-center">
        <div data-testid="login-brand" className="flex flex-col items-center mb-3 leading-tight">
          <p data-testid="login-brand-presents" className="text-[10px] sm:text-[11px] font-medium uppercase tracking-[0.32em] text-[#2B2140]/55">
            Presents
          </p>
          <p data-testid="login-brand-vownex" className="mt-1 text-sm sm:text-base font-extrabold uppercase tracking-[0.18em] text-[#2B2140]">
            Vownex Innovations
          </p>
        </div>
        <img data-testid="login-logo-full" src="/assets/readbuddy-logo.png" alt="ReadBuddy"
          className="w-56 sm:w-72 mx-auto mb-6 select-none pointer-events-none"/>

        {/* Iter-51.14: dropped the "Helping your child read confidently…"
            tagline. ReadBuddy is now positioned for readers of every
            age (kids, teens, adults) — see the Who-is-this-for page —
            so a child-specific tagline on the door misrepresents the
            product. The lockup and taglines inside the logo art still
            carry the brand voice ("Your Reading. Your Buddy."). */}

        {/* Email + password login — placed above the Google button so
            parents who registered an account (iter-46) can sign in
            without hunting for the form. */}
        <form onSubmit={handleEmailLogin} className="w-full bg-white/95 backdrop-blur rounded-3xl p-5 shadow-xl space-y-3 text-left">
          <label className="flex items-center gap-3 px-4 rounded-2xl border-2 border-slate-200 bg-white focus-within:border-[#FF6B1A]">
            <Mail size={18} className="text-slate-400"/>
            <input data-testid="login-email" type="email" required placeholder="Email address"
              value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
              className="flex-1 bg-transparent outline-none py-3 text-[#2B2140]"/>
          </label>
          <label className="relative flex items-center gap-3 pl-4 pr-14 rounded-2xl border-2 border-slate-200 bg-white focus-within:border-[#FF6B1A]">
            <Lock size={18} className="text-slate-400"/>
            <input data-testid="login-password" type={showPassword ? 'text' : 'password'} required placeholder="Password"
              value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
              className="flex-1 min-w-0 bg-transparent outline-none py-3 text-[#2B2140]"/>
            <button data-testid="login-password-toggle" type="button" onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-1 top-1/2 -translate-y-1/2 min-w-11 min-h-11 flex items-center justify-center text-slate-600 hover:text-[#2B2140]">
              {showPassword ? <EyeOff className="w-[1.5em] h-[1.5em]"/> : <Eye className="w-[1.5em] h-[1.5em]"/>}
            </button>
          </label>
          {error && <p data-testid="login-error" className="text-sm text-rose-600">{error}</p>}
          <button data-testid="email-login-btn" type="submit" disabled={busy}
            className="pushable w-full bg-[#FF6B1A] text-white text-lg font-bold py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin"/>}
            Sign in
          </button>
          <div className="flex items-center justify-between text-xs font-bold pt-1">
            <Link data-testid="login-signup-link" to="/register" className="text-[#5B8DEF]">Create account</Link>
            <Link data-testid="login-forgot-link" to="/forgot-password" className="text-slate-500 hover:text-slate-800">Forgot password?</Link>
          </div>
        </form>

        <div className="flex items-center gap-3 my-5 w-full max-w-xs" data-testid="login-divider">
          <div className="h-px bg-slate-300/70 flex-1"/>
          <span className="text-xs font-bold uppercase text-slate-500 tracking-widest">or</span>
          <div className="h-px bg-slate-300/70 flex-1"/>
        </div>

        <button
          data-testid="google-login-btn"
          onClick={handleGoogle}
          className="pushable pushable-primary bg-white border-2 border-[#2B2140] text-[#2B2140] text-base font-bold px-8 py-3 rounded-full inline-flex items-center gap-3"
        >
          {t('signInGoogle')}
        </button>
        {/* Iter-52.23 — phone-OTP sign in. Reuses the same MSG91
            widget as signup. When the user taps the button we swap
            the whole card to a widget-only view so they don't get
            visually confused between email/password and phone OTP. */}
        {phoneOnlyEnabled && !phoneMode && (
          <button
            type="button"
            data-testid="phone-otp-login-btn"
            onClick={() => { setPhoneError(''); setPhoneMode(true); }}
            className="pushable mt-3 bg-[#25D366] text-white text-base font-bold px-8 py-3 rounded-full inline-flex items-center gap-3"
          >
            <Phone size={18} strokeWidth={2.6}/> Sign in with phone OTP
          </button>
        )}
        {phoneMode && (
          <div data-testid="phone-otp-login-panel" className="mt-4 w-full max-w-xs bg-white/95 backdrop-blur rounded-3xl p-5 shadow-lg border border-slate-200">
            <button
              onClick={() => { setPhoneMode(false); setPhoneError(''); }}
              data-testid="phone-otp-back-btn"
              className="text-xs font-bold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3"
            >
              <ArrowLeft size={12}/> Use email &amp; password instead
            </button>
            <p className="text-sm font-bold text-[#2B2140] mb-1 flex items-center gap-1.5">
              <Phone size={14} strokeWidth={2.8} className="text-[#25D366]"/> Sign in with phone OTP
            </p>
            <p className="text-xs text-slate-500 mb-3">
              Tap below → verify with a 4-digit SMS OTP → you'll be signed in.
            </p>
            {phoneBusy ? (
              <div className="flex items-center gap-2 text-slate-600 text-sm">
                <Loader2 size={14} className="animate-spin"/> Signing you in…
              </div>
            ) : (
              <Msg91Widget
                key={phoneWidgetKey}
                onVerified={onPhoneOtpVerified}
                onError={(msg) => setPhoneError(msg || 'Verification failed. Please try again.')}
                label="Verify your mobile"
              />
            )}
            {phoneError && (
              <div data-testid="phone-otp-login-error" className="mt-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold px-3 py-2">
                {phoneError}
                <button
                  onClick={() => { setPhoneError(''); setPhoneWidgetKey(k => k + 1); }}
                  className="mt-1 block text-[11px] font-bold text-rose-900 underline"
                >
                  Try again with a fresh OTP
                </button>
              </div>
            )}
          </div>
        )}
        {/* Iter-51.14: removed the "Parent account login" caption — the
            product now serves all ages, not just parents-of-children. */}
      </div>

      <p data-testid="login-copyright" className="absolute bottom-6 inset-x-0 z-10 text-center text-[11px] font-semibold text-[#2B2140]/60">
        © 2026 Vownex Innovations. All rights reserved.
      </p>
    </div>
  );
}
